import {
  StatusWithNoneAndCancellation,
  ShiftEvent,
  BaseEvent,
  User,
  isParameterChange,
  isStoppage,
  isSystemChangeRequest,
  isTask,
  Shift,
  ApprovalStatus,
  isSpot,
  isStoppageTypeWithCode,
  isStoppageTypeWithReason,
  usePlantConfig
} from '@domain/shifthandover'
import {getStoppageDuration} from '@domain/stoppages'
import {ContentBox} from '@hconnect/common/components/ContentBox'
import {TaskRepetitionLabel} from '@hconnect/common/components/shiftEventDetails/TaskRepetitionLabel'
import {
  PriorityTitle,
  EventProcessStageTitle
} from '@hconnect/common/components/shiftEventFormFields'
import {Tag} from '@hconnect/common/components/Tag'
import {useKpiNodeIds} from '@hconnect/common/hooks'
import {
  RootCauseFailureAnalyses,
  StatusWithNoneAndCancellationAndApproverAndWorkOrderStates,
  WorkOrderEventStatus
} from '@hconnect/common/types'
import {getMaintainAnalysisUrl, getAssigneesValue, getRequiredShift} from '@hconnect/common/utils'
import {formatDateTime} from '@hconnect/common/utils/datetime.utils'
import {
  generateMarkdownOptions,
  getHighlightedTitle,
  getHighlightedTitleWithLink
} from '@hconnect/common/utils/highlightHelpers'
import {DateFormat} from '@hconnect/uikit'
import {MarkdownText} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {useCommonFeatureFlag, FeatureFlagKey} from '@hproduce/feature-flags'
import CloseIcon from '@mui/icons-material/Close'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import {Typography, Grid, Box, Link} from '@mui/material'
import {UseMutationResult} from '@tanstack/react-query'
import {ApproversList} from '@widgets/common-approvers'
import {
  EventTypeLabel,
  getEquipmentLabelDetails,
  EventStatusLabel
} from '@widgets/common-shift-event'
import {AxiosError} from 'axios'
import {TFunction} from 'i18next'
import {cloneDeep, get, noop} from 'lodash'
import moment from 'moment-timezone'
import React, {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {isWorkOrderStatus, isEventWithStatusNone} from '../common/utils/eventStatus'
import {SpotInspectionDetails, EventInfo, EventActionList, SubmitSection} from '../components'
import {TaskResolvedMandatoryCommentDialog} from '../components/dialogs/TaskResolvedMandatoryCommentDialog'
import {ApproversBanner} from '../components/eventProperties'
import {EventStatusDropDown} from '../components/eventProperties/EventStatus'
import {FunctionTitle} from '../components/eventProperties/ParameterChangeFunctionDropdown'
import {ParameterChangeStatusDropDown} from '../components/eventProperties/ParameterChangeStatusDropDown'
import {WorkOrderStatusDropDown} from '../components/eventProperties/WorkOrderEventStatus'
import {regularSize, SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../consts'
import {useEventUpdateStatus} from '../hooks'
import {getStoppageDescription} from '../utils'

export type Props = {
  plantId: string
  item: ShiftEvent
  actions?: {
    onSapNotificationCreated(): void
    doClose(): void
    doStoppageSplit(item: Partial<ShiftEvent>): void
    doEdit(item: ShiftEvent): void
  }
  refetchComments?(): void
  disableSap?: boolean
  isSeriesPreview?: boolean
  freeSearchText?: string
  hasChecklists?: boolean
  children?: React.ReactNode
  disableStatus?: boolean
  disableConfig?: boolean
  useApprovalHook: (params: {
    plantId: string
  }) => UseMutationResult<unknown, AxiosError, {eventId: string; approvalStatus: ApprovalStatus}>
  onSapPmNotification?: (timezone: string) => void
}

const EventHighlightLabel: React.FC<{value?: boolean; 'data-test-id': string}> = ({
  value,
  'data-test-id': dataTestId
}) => {
  return value ? <StarBorderIcon data-test-id={dataTestId} /> : null
}

function getUserValues(item: ShiftEvent, t: TFunction, key: string): string | null {
  const assignees: User[] = cloneDeep(get(item, key))
  return getAssigneesValue(assignees, t)
}

const DueDateLabel: React.FC<{
  item: ShiftEvent
  plantId: string
  shifts: Shift[]
  fallback: string
}> = ({item, plantId, shifts, fallback}) => {
  const timezone = useUpmPlantTimezone(plantId)
  if (!isTask(item) || !item.dueDate) return <>{fallback}</>

  const dueDateShift = getRequiredShift(item.dueDate, shifts)

  if (item.doNotStartBefore) {
    const endShift = getRequiredShift(item.doNotStartBefore, shifts)
    return (
      <>
        <DateFormat date={endShift.startDate} timezone={timezone} /> -{' '}
        <DateFormat date={dueDateShift.startDate} timezone={timezone} />
      </>
    )
  }

  return (
    <>
      <DateFormat date={dueDateShift.startDate} timezone={timezone} /> {dueDateShift.name}
    </>
  )
}

const categoryLabel = (item: ShiftEvent, t: TFunction) =>
  item.category ? t(`shiftEvent.category.${item.category}`) : t('shiftEvent.action.noneOfThat')

const getEventTile = (title: string, freeSearchText?: string) =>
  freeSearchText ? getHighlightedTitle(title, freeSearchText) : title

// eslint-disable-next-line complexity
export const EventDetails: React.FC<Props> = ({
  plantId,
  item,
  actions,
  freeSearchText,
  isSeriesPreview,
  refetchComments,
  disableStatus,
  disableConfig,
  children,
  useApprovalHook,
  ...rest
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const isStoppageSubmitSupported = useCommonFeatureFlag(FeatureFlagKey.SubmitStoppage)
  const {data: config} = usePlantConfig({plantId, language, options: {enabled: !disableConfig}})
  const {
    shifts = [],
    kilnStoppageCodes = [],
    rawMillStoppageCodes = [],
    finishMillStoppageCodes = [],
    alternativeFuelInstallationStoppageCodes = [],
    workOrderTarget
  } = config ?? {}
  const timezone = useUpmPlantTimezone(plantId)
  const updateAction =
    useEventUpdateStatus<StatusWithNoneAndCancellationAndApproverAndWorkOrderStates>({plantId})
  const departments = useKpiNodeIds(plantId, undefined, isStoppage(item))

  const [maintenanceNotificationFailed, setMaintenanceNotificationFailed] = useState<boolean>(false)
  const [isMandatoryCommentDialogOpen, setIsMandatoryCommentDialogOpen] = useState<boolean>(false)
  const [targetStatus, setTargetStatus] = useState<StatusWithNoneAndCancellation | null>(null)

  const handleTaskStatusChange = useCallback(
    (newStatus: StatusWithNoneAndCancellation) => {
      const isTaskResolved =
        item.status !== newStatus && (newStatus === 'cancelled' || newStatus === 'done')

      if (isTask(item) && isTaskResolved) {
        setTargetStatus(newStatus)
        setIsMandatoryCommentDialogOpen(true)
      } else {
        updateAction.mutate({task: item, status: newStatus})
        setTargetStatus(null)
      }
    },
    [item, updateAction]
  )

  const handleTaskMandatoryCommentSubmit = useCallback(
    (taskResolvedComment: string) => {
      if (isTask(item) && targetStatus) {
        updateAction.mutate(
          {
            task: item,
            status: targetStatus,
            taskResolvedComment
          },
          {
            onSuccess: () => {
              refetchComments?.()
            },
            onError: (err) => {
              console.error('Update failed:', err)
            }
          }
        )
        setIsMandatoryCommentDialogOpen(false)
        setTargetStatus(null)
      }
    },
    [item, targetStatus, updateAction, refetchComments]
  )

  const assigneesValue: string | null = useMemo(
    () => getUserValues(item, t, 'assignees'),
    [item, t]
  )

  const departmentLabel =
    item.upmDepartmentId && departments.find(({id}) => id === item.upmDepartmentId)?.label

  const getStatusField = useCallback(() => {
    if (isWorkOrderStatus(item.status)) {
      return (
        <WorkOrderStatusDropDown
          translate={t}
          disabled
          onChange={() => {
            // disabled/impossible
          }}
          data-test-id="event-detail-work-order-status"
          value={item.status as WorkOrderEventStatus}
        />
      )
    }
    if (isStoppage(item)) {
      return (
        <Tag label={t('shiftEvent.label.status')} data-test-id="event-detail-status">
          {item.status && <EventStatusLabel status={item.status} />}
        </Tag>
      )
    }
    if (isParameterChange(item) || isSystemChangeRequest(item)) {
      return (
        <ParameterChangeStatusDropDown
          translate={t}
          eventData={item}
          data-test-id="event-detail-status"
          disabled={disableStatus}
          onChange={(value) => {
            updateAction.mutate({task: item, status: value})
          }}
        />
      )
    }
    if (isSpot(item)) {
      return (
        <EventStatusDropDown
          translate={t}
          allow={['none']}
          data-test-id="event-detail-status"
          onChange={(value) => {
            updateAction.mutate({task: item, status: value})
          }}
          value={item.status}
        />
      )
    }
    if (isTask(item) && isSeriesPreview) {
      return (
        <EventStatusDropDown
          translate={t}
          data-test-id="event-detail-status"
          disabled={true}
          onChange={noop}
          value={item.status}
        />
      )
    }

    if (isTask(item)) {
      return (
        <>
          <EventStatusDropDown
            translate={t}
            allow={['cancelled']}
            data-test-id="event-detail-status"
            onChange={handleTaskStatusChange}
            value={item.status}
          />
          {isMandatoryCommentDialogOpen && (
            <TaskResolvedMandatoryCommentDialog
              open={isMandatoryCommentDialogOpen}
              onClose={() => setIsMandatoryCommentDialogOpen(false)}
              onSubmit={handleTaskMandatoryCommentSubmit}
            />
          )}
        </>
      )
    }

    return (
      <EventStatusDropDown
        translate={t}
        allow={isEventWithStatusNone(item as BaseEvent) ? ['none', 'cancelled'] : ['cancelled']}
        data-test-id="event-detail-status"
        onChange={(value) => {
          updateAction.mutate({task: item, status: value})
        }}
        value={item.status}
      />
    )
  }, [
    item,
    isSeriesPreview,
    updateAction,
    disableStatus,
    isMandatoryCommentDialogOpen,
    handleTaskStatusChange,
    handleTaskMandatoryCommentSubmit,
    t
  ])

  return (
    <ContentBox
      data-test-id="events-details"
      mode="max100PercentOfParentHeight"
      bodyWithPadding
      title={
        <Typography
          component={'span'}
          variant={'h2'}
          sx={{textTransform: 'none'}}
          data-test-id="event-detail-title"
          display="flex"
          alignItems="center"
        >
          <EventHighlightLabel
            value={item.isHighlighted}
            data-test-id="event-detail-title-highlight"
          />
          {getEventTile(item.title, freeSearchText)}
        </Typography>
      }
      afterTitle={
        actions && (
          <Box display="flex" alignItems="center">
            <EventActionList
              {...rest}
              {...actions}
              onlyDeleteSeries={isSeriesPreview}
              item={item}
              maintenanceNotificationFailed={maintenanceNotificationFailed}
              setMaintenanceNotificationFailed={setMaintenanceNotificationFailed}
              plantId={plantId}
              onSapPmNotification={rest.onSapPmNotification}
            />
            <Typography
              sx={{
                p: 1.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <CloseIcon
                onClick={() => actions.doClose()}
                color="primary"
                data-test-id="events-details-close"
              />
            </Typography>
          </Box>
        )
      }
      headerSx={{paddingBottom: 0}}
    >
      <EventInfo
        plantId={plantId}
        workOrderTarget={workOrderTarget}
        item={item}
        sapNotificationFailed={maintenanceNotificationFailed}
      />

      {isStoppage(item) && isStoppageSubmitSupported && (
        <SubmitSection isSubmitted={item.isSubmitted} sx={{mt: 2}} />
      )}

      <Grid
        container
        sx={{
          mt: isStoppage(item) && isStoppageSubmitSupported ? 0 : 1.5,
          mb: 1.5
        }}
        spacing={2}
      >
        <Grid item {...regularSize}>
          <Tag label={t('shiftEvent.label.eventType')} data-test-id="event-detail-eventType">
            <EventTypeLabel eventType={item.eventType} />
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          {getStatusField()}
        </Grid>
        <Grid item {...regularSize}>
          <Tag label={t('shiftEvent.label.category')} data-test-id="event-detail-category">
            {categoryLabel(item, t)}
          </Tag>
        </Grid>
        {!isTask(item) && (
          <Grid item {...regularSize} data-test-id="event-detail-priority">
            <Tag label={t('shiftEvent.label.priority')}>
              {!item.priority ? (
                t('shiftEvent.action.noneOfThat')
              ) : (
                <PriorityTitle priority={item.priority} />
              )}
            </Tag>
          </Grid>
        )}

        {isStoppage(item) && (
          <>
            <Grid item xs={12}>
              {isStoppageTypeWithReason(item.stoppageType) && 'stoppageReason' in item && (
                <Tag
                  label={t('shiftEvent.label.stoppageReason')}
                  data-test-id="event-detail-stoppageReason"
                >
                  {item.stoppageReason || t('shiftEvent.action.noneOfThat')}
                </Tag>
              )}
              {isStoppageTypeWithCode(item.stoppageType) && 'stoppageCode' in item && (
                <Tag
                  label={t('shiftEvent.label.stoppageCode')}
                  data-test-id="event-detail-stoppageCode"
                >
                  {item.stoppageCode}{' '}
                  {getStoppageDescription(item, {
                    kiln: kilnStoppageCodes,
                    cementMill: finishMillStoppageCodes,
                    rawMill: rawMillStoppageCodes,
                    alternativeFuelInstallation: alternativeFuelInstallationStoppageCodes
                  })}
                </Tag>
              )}
            </Grid>
            {departmentLabel && (
              <Grid item xs={12}>
                <Tag
                  data-test-id="event-detail-stopped-main-equipment"
                  label={t('shiftEvent.label.stoppedMainEquipment')}
                >
                  {departmentLabel}
                </Tag>
              </Grid>
            )}
            <Grid item xs={6}>
              <Tag
                label={t('shiftEvent.label.stoppageStart')}
                data-test-id="event-detail-stoppageStart"
              >
                {formatDateTime(item.stoppageStart) || t('shiftEvent.action.noneOfThat')}
              </Tag>
            </Grid>
            <Grid item xs={6}>
              <Tag
                label={t('shiftEvent.label.stoppageEnd')}
                data-test-id="event-detail-stoppageEnd"
              >
                {formatDateTime(item.stoppageEnd)}
              </Tag>
            </Grid>
            <Grid item xs={6}>
              <Tag label={t('shiftEvent.label.duration')} data-test-id="event-detail-duration">
                {item.stoppageStart
                  ? getStoppageDuration(
                      item.stoppageStart.toISOString(),
                      item.stoppageEnd?.toISOString()
                    )
                  : '-'}
              </Tag>
            </Grid>
          </>
        )}

        {isSystemChangeRequest(item) && (
          <Grid item {...regularSize} data-test-id="event-detail-functionId">
            <Tag label={t('shiftEvent.label.function')}>
              {!item.parameterChange?.functionId ? (
                t('shiftEvent.action.noneOfThat')
              ) : (
                <FunctionTitle functionId={item.parameterChange.functionId} />
              )}
            </Tag>
          </Grid>
        )}

        <Grid item xs={12}>
          {isStoppage(item) && (
            <Typography variant="h5" fontWeight={600} mt={1.5} mb={3.5}>
              {t('shiftEvent.label.rootCausingEquipment')}
            </Typography>
          )}
          <Tag
            data-test-id="event-detail-main-equipment"
            label={t('shiftEvent.label.mainEquipment')}
          >
            {getEquipmentLabelDetails(item.mainEquipment, false)}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag data-test-id="event-detail-equipment" label={t('shiftEvent.label.equipment')}>
            {getEquipmentLabelDetails(item.equipment)}
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag label={t('shiftEvent.label.processStage')} data-test-id="event-detail-processStage">
            {!item.processStage ? (
              t('shiftEvent.action.noneOfThat')
            ) : (
              <EventProcessStageTitle plantId={plantId} stage={item.processStage} />
            )}
          </Tag>
        </Grid>
        {!isTask(item) && !isStoppage(item) && !!item.schedule && (
          <Grid item xs={12}>
            <Tag
              data-test-id="event-detail-schedule"
              label={t(
                isSystemChangeRequest(item)
                  ? 'shiftEvent.label.temporary'
                  : 'shiftEvent.label.schedule'
              )}
            >
              {moment(item.schedule.startDate).isSame(item.schedule.endDate, 'day') ? (
                formatDateTime(item.schedule.startDate)
              ) : (
                <>
                  <DateFormat date={item.schedule.startDate} timezone={timezone} />
                  {item.schedule.endDate && (
                    <>
                      {' '}
                      - <DateFormat date={item.schedule.endDate} timezone={timezone} />
                    </>
                  )}
                </>
              )}
            </Tag>
          </Grid>
        )}
        {isTask(item) && (
          <>
            <Grid item {...regularSize}>
              <Tag label={t('shiftEvent.label.dueDate')} data-test-id="event-detail-dueDate">
                <DueDateLabel
                  item={item}
                  plantId={plantId}
                  shifts={shifts}
                  fallback={t('shiftEvent.action.noneOfThat')}
                />
              </Tag>
            </Grid>

            <Grid item {...regularSize}>
              <Tag label={t('shiftEvent.label.priority')} data-test-id="event-detail-priority">
                {item.priority ?? t('shiftEvent.action.noneOfThat')}
              </Tag>
            </Grid>

            <Grid item {...regularSize}>
              <Tag label={t('shiftEvent.label.assignee')} data-test-id="event-detail-assigneeName">
                {assigneesValue || t('shiftEvent.action.noneOfThat')}
              </Tag>
            </Grid>

            {item.repetitionInfo && (
              <Grid item xs={12}>
                <Tag label={t('shiftEvent.label.recurring')}>
                  <TaskRepetitionLabel
                    settings={item.repetitionInfo}
                    defaultStartDate={item.dueDate}
                    omitFirstOccurrence
                    plantId={plantId}
                    timezone={timezone}
                  />
                </Tag>
              </Grid>
            )}
          </>
        )}

        {(isParameterChange(item) || isSystemChangeRequest(item)) && (
          <>
            <Grid item xs={12}>
              <Tag
                label={t('shiftEvent.label.currentValue')}
                data-test-id="event-detail-parameter-current"
              >
                {item.parameterChange?.currentValue || '-'}
              </Tag>
            </Grid>
            <Grid item xs={12}>
              <Tag label={t('shiftEvent.label.newValue')} data-test-id="event-detail-parameter-new">
                {item.parameterChange?.newValue || '-'}
              </Tag>
            </Grid>
            <ApproversBanner
              plantId={plantId}
              approvers={item.approvers}
              eventId={item.id}
              useApprovalHook={useApprovalHook}
            />
            <Grid item xs={12}>
              <ApproversList approvers={item.approvers} />
            </Grid>
          </>
        )}

        {isSpot(item) && item.spotInspections && (
          <Grid item xs={12}>
            <SpotInspectionDetails spotInspections={item.spotInspections} />
          </Grid>
        )}

        <Grid item xs={12}>
          <Tag
            label={t('shiftEvent.label.description')}
            descriptionSx={{fontWeight: 400, fontSize: 14}}
            data-test-id="event-detail-description"
          >
            {item.description ? (
              <MarkdownText options={generateMarkdownOptions(freeSearchText)}>
                {freeSearchText
                  ? getHighlightedTitleWithLink(item.description, freeSearchText, true)
                  : item.description}
              </MarkdownText>
            ) : (
              t('shiftEvent.action.noneOfThat')
            )}
          </Tag>
        </Grid>

        {isStoppage(item) && item.rootCauseFailureAnalyses && (
          <Grid item xs={12}>
            <Tag
              label={t('shiftEvent.label.rootCauseFailureAnalyses')}
              descriptionSx={{fontWeight: 400, fontSize: 14}}
              data-test-id="event-detail-rootCauseFailureAnalyses"
            >
              <Box display="flex" flexDirection="column">
                {item.rootCauseFailureAnalyses.map(
                  ({title, id}: Partial<RootCauseFailureAnalyses>) => (
                    <Link
                      key={id}
                      href={getMaintainAnalysisUrl(plantId, id)}
                      underline="none"
                      fontWeight={600}
                      width="fit-content"
                    >
                      {title}
                    </Link>
                  )
                )}
              </Box>
            </Tag>
          </Grid>
        )}
      </Grid>

      {children}
    </ContentBox>
  )
}
